import { cloneDeep, findIndex, flatten, map } from 'lodash';

import backToListView from 'src/actions/shared/backToListView';
import { DomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { toastError, toastSuccess } from 'src/actions/toast';
import { translateErrors } from 'src/adapters/validateType';
import * as sharedTypes from 'src/actions/shared/sharedTypes';
import createCollectionResource, { UpdateOptions } from 'src/actions/shared/createResource';
import EmailDomain from 'src/models/Email/EmailDomain';
import emptyObserverCreator from 'src/actions/shared/emptyObserverCreator';
import logout from 'src/actions/auth/logout';
import SenderName from 'src/models/Email/SenderName';

const emailSenderNamesResource = createCollectionResource<SenderName>({
  endpoint: 'v1/orgs/:orgId/email-domains/:accountId/sender-names',
  name: 'emailSenderNames',
  observerCreator: emptyObserverCreator,
  type: SenderName,
  reducers: {
    updateSuccess(state, action) {
      state.persistingState = 'succeeded';
      const newValues = cloneDeep(state.values);
      const index = findIndex(newValues, ({ address }) => address === action.payload.address);
      if (index === -1) {
        newValues.push(action.payload);
      } else {
        newValues[index] = action.payload;
      }

      state.values = newValues;
    },
  },
});

const fetchAll: sharedTypes.AppThunk =
  (emailDomains: EmailDomain[]) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(emailSenderNamesResource.actions.fetchStarted({}));

      const senderNames = await Promise.all(
        map(emailDomains, emailDomain => gateways.emailDomains.getSenderNames({ accountId: emailDomain.id }))
      );

      dispatch(emailSenderNamesResource.actions.fetchSuccess(flatten(senderNames)));
    } catch (err) {
      if (err instanceof DomainError) {
        dispatch(emailSenderNamesResource.actions.fetchError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(emailSenderNamesResource.actions.fetchRequestError({}));
      }
    }
  };

const updateSenderName: sharedTypes.AppThunk =
  (payload: any, options: UpdateOptions) =>
  async (dispatch, getState, { gateways }) => {
    try {
      const senderNamePayload = {
        address: payload.address,
        senderName: payload.senderName,
      };
      dispatch(updateStarted({}));
      await gateways.emailSenderNames.replace({ accountId: payload.accountId }, senderNamePayload);
      dispatch(updateSuccess(senderNamePayload));
      dispatch(toastSuccess(`Updated sender name for "${payload.address}"`));
      if (!options?.disableWindowBack) {
        backToListView();
      }
    } catch (err) {
      if (err instanceof DomainError) {
        dispatch(addError(translateErrors(err.errors)));
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(addRequestError(err));
      }
      dispatch(toastError('There was an error while updating'));
    }
  };

emailSenderNamesResource.effects.fetchAll = fetchAll;
emailSenderNamesResource.effects.updateSenderName = updateSenderName;

const { updateStarted, updateSuccess, addError, addRequestError } = emailSenderNamesResource.actions;

export default emailSenderNamesResource;
